import { useTranslation } from "react-i18next";
import { Form, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { createFormHelper } from "lib/Form";
import { useUpdateThreeDepartmentMutation } from "modules/AppointmentDetails/hooks";
import { getUserFullName } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment } from "models";

type ThreeDepartmentFormValues = Pick<Appointment, "three_department_receptionist" | "three_department_parts" | "three_department_workshop" | "three_department_remark">;

type ThreeDepartmentModalProps = {
  appointment: Appointment;
  readOnly?: boolean;
  onClose: () => void;
};

const THREE_DEPARTMENT_FORM_ID = "three-department-form";

const FormHelper = createFormHelper<ThreeDepartmentFormValues>();

enum LOG_TYPE {
  Checked = 1,
  Unchecked,
  Remark
}

const getThreeDepartmentLog = (appointment: Appointment) => {
  const logs = [];

  if (appointment.three_department_receptionist_user_id) {
    logs.push({
      key: "receptionist",
      icon: "user",
      type: appointment.three_department_receptionist ? LOG_TYPE.Checked : LOG_TYPE.Unchecked,
      user: appointment.three_department_receptionist_user,
      date: appointment.three_department_receptionist_set_at
    });
  }

  if (appointment.three_department_parts_user_id) {
    logs.push({
      key: "parts",
      icon: "gear",
      type: appointment.three_department_parts ? LOG_TYPE.Checked : LOG_TYPE.Unchecked,
      user: appointment.three_department_parts_user,
      date: appointment.three_department_parts_set_at
    });
  }

  if (appointment.three_department_workshop_user_id) {
    logs.push({
      key: "workshop",
      icon: "wrench",
      type: appointment.three_department_workshop ? LOG_TYPE.Checked : LOG_TYPE.Unchecked,
      user: appointment.three_department_workshop_user,
      date: appointment.three_department_workshop_set_at
    });
  }

  if (appointment.three_department_remark_user_id) {
    logs.push({
      key: "remark",
      type: LOG_TYPE.Remark,
      user: appointment.three_department_remark_user,
      date: appointment.three_department_remark_updated_at
    });
  }

  return logs.sort((a, b) => {
    if (!a.date && b.date) return -1;
    if (a.date && !b.date) return 1;
    if (a.date && b.date) return b.date.localeCompare(a.date);
    return 0;
  });
};

export const ThreeDepartmentModal = ({ appointment, readOnly, onClose }: ThreeDepartmentModalProps) => {
  const { t } = useTranslation();

  const { mutateAsync: updateThreeDepartment, isPending } = useUpdateThreeDepartmentMutation();

  const handleSave = async ({ three_department_receptionist, three_department_parts, three_department_workshop, three_department_remark }: ThreeDepartmentFormValues) => {
    await updateThreeDepartment({
      appointment_id: appointment.id,
      receptionist: three_department_receptionist !== appointment.three_department_receptionist ? three_department_receptionist : null,
      parts: three_department_parts !== appointment.three_department_parts ? three_department_parts : null,
      workshop: three_department_workshop !== appointment.three_department_workshop ? three_department_workshop : null,
      remark: three_department_remark
    });

    onClose();
  };

  const logs = getThreeDepartmentLog(appointment);

  return (
    <Modal open onClose={onClose} size="small" className="ThreeDepartmentModal">
      <Modal.Header className="padding-container">{t("v8_three_department_note", "3 Departments Check Note")}</Modal.Header>
      <Modal.Content className="padding-container">
        <FormHelper.Form
          formProps={{ id: THREE_DEPARTMENT_FORM_ID }}
          onSubmit={handleSave}
          defaultValues={{
            three_department_receptionist: appointment.three_department_receptionist,
            three_department_parts: appointment.three_department_parts,
            three_department_workshop: appointment.three_department_workshop,
            three_department_remark: appointment.three_department_remark
          }}
        >
          <div className="checkboxes">
            <div className="checkbox-cell">
              <FormHelper.Field.Checkbox name="three_department_receptionist" disabled={readOnly} />
              <Icon className="user" />
              <span>{t("v8_reception", "Reception")}</span>
            </div>
            <div className="checkbox-cell">
              <FormHelper.Field.Checkbox name="three_department_parts" disabled={readOnly} />
              <Icon className="gear" />
              <span>{t("v8_parts", "Parts")}</span>
            </div>
            <div className="checkbox-cell">
              <FormHelper.Field.Checkbox name="three_department_workshop" disabled={readOnly} />
              <Icon className="wrench" />
              <span>{t("v8_workshop", "Workshop")}</span>
            </div>
          </div>

          <div className="remark-section">
            <label>{t("v8_remark", "Remark")}</label>
            <FormHelper.Field.TextArea name="three_department_remark" textAreaProps={{ rows: 3, disabled: readOnly, placeholder: t("v8_add_remark", "Add remark") }} />
          </div>
        </FormHelper.Form>

        <div className="log-section">
          {logs.map(log => (
            <span key={log.key} className="log-entry">
              {log.icon && <Icon className={log.icon} />}
              {log.type === LOG_TYPE.Checked && t("v8_checked_by", "Checked by")}
              {log.type === LOG_TYPE.Unchecked && t("v8_unchecked_by", "Unchecked by")}
              {log.type === LOG_TYPE.Remark && t("v8_remark_added_by", "Remark added by")} {getUserFullName(log.user)}
              {log.date && (
                <>
                  {` ${t("v8_on", "on")}`} {formatDate(log.date, DATE_FORMATS_FNS.dateMonthYearTime)}
                </>
              )}
            </span>
          ))}
        </div>
      </Modal.Content>

      <Modal.Actions className="padding-container">
        <div className="save-cancel-buttons-container">
          <Form.Button className="light" onClick={onClose} disabled={isPending}>
            {t("v8_cancel", "Cancel")} <Icon className="xmark" />
          </Form.Button>
          <Form.Button color="green" form={THREE_DEPARTMENT_FORM_ID} type="submit" loading={isPending} disabled={readOnly || isPending}>
            {t("v8_save", "Save")} <Icon className="check" />
          </Form.Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
